import {
  BarsOutlined,
  DownloadOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import {
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Row,
  Segmented,
  Skeleton,
  Typography,
  theme,
  Button,
  Tooltip,
} from "antd";
import { useState, useContext, useEffect } from "react";
import CustomLineChart from "../../components/common/CustomLineChart";
import ParameterTrendFilter from "../../components/trending/ParameterTrendFilter";
import dayjs from "dayjs";
import SummaryTable from "../../components/trending/SummaryTable";
import TrendTableView from "../../components/trending/TrendTableView";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";

const { useToken } = theme;

function ParameterTrendPage() {
  const { token } = useToken();
  const { Title } = Typography;
  const [trendView, setTrendView] = useState("Trend");
  const [filterForm] = Form.useForm();
  const [filterValues, setFilterValues] = useState(null);

  const [fetchWrapper] = useFetch();
  const { siteSelected, messageApi } = useContext(IndexContext);
  const [loading, setLoading] = useState(false);

  // parameter trend data
  const [parameterTrendData, setParameterTrendData] = useState([]);
  const [parameterTrendSummaryData, setParameterTrendSummaryData] = useState(
    []
  );
  const [parameterTrendTableData, setParameterTrendTableData] = useState([]);

  const filename = `ParameterTrend_${dayjs().format(
    "YYYY-MM-DDTHH:mm:ss"
  )}.xlsx`;

  const applyFilter = (value) => {
    setFilterValues(value);
  };

  const resetFilter = () => {
    setFilterValues(null);
  };

  /* API to get parameter trend data */
  function getParameterTrendData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "trending/getParameterTrendData",
      body: {
        site_id: siteSelected,
        start_date_time: filterValues.start.format("YYYY-MM-DD HH:mm:ss"),
        end_date_time: filterValues.end.format("YYYY-MM-DD HH:mm:ss"),
        devices: filterValues.device,
        parameter_tag_keys: filterValues.parameter,
      },
      onSuccess: (fetch_output) => {
        setLoading(false);
        if (fetch_output.data?.not_found_msg != "") {
          for (let key in fetch_output.data.not_found_msg) {
            messageApi.open({
              type: "warning",
              content: fetch_output.data.not_found_msg[key],
            });
          }
        }
        setParameterTrendData(fetch_output.data.parameter_trend_data);
        setParameterTrendSummaryData(fetch_output.data.parameter_summary_data);
        setParameterTrendTableData(fetch_output.data.parameter_table_data);
      },
    });
  }

  // function to download the file
  function downloadFile() {
    return new Promise((resolve, reject) => {
      fetchWrapper({
        endpoint_url: "trending/downloadFile",
        body: {
          table_data: parameterTrendTableData,
          summary_data: parameterTrendSummaryData,
          table_data_sheet_name: "Parameter Demand Trend",
          summary_data_sheet_name: "Parameter Demand Summary",
        },
        json_output: false,
        onSuccess: (response) => {
          // returned a json response.
          if (response.headers.get("content-type") === "application/json") {
            response.json().then((data) => {
              if (Object.keys(data).includes("error")) {
                // has issue with the download file
                messageApi.open({
                  type: "error",
                  content: "Failed to download file",
                });
                resolve(false);
              } else {
                // returned something else.
                messageApi.open({
                  type: "error",
                  content: "Failed to download file.",
                });
                resolve(false);
              }
            });
          } else {
            // no issue with the response. returned a spreadsheet
            response.blob().then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              resolve(true);
            });
          }
        },
        onResponseError: (err) => {
          messageApi.open({
            type: "error",
            content: "Failed to download file",
          });
          resolve(false);
        },
      });
    });
  }

  useEffect(() => {
    if (filterValues && siteSelected) {
      getParameterTrendData();
    } else {
      setParameterTrendData([]);
      setParameterTrendSummaryData([]);
      setParameterTrendTableData([]);
    }
  }, [siteSelected, filterValues]);

  useEffect(() => {
    setParameterTrendData([]);
    setParameterTrendSummaryData([]);
    setParameterTrendTableData([]);
    setFilterValues(null);
  }, [siteSelected]);

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <ParameterTrendFilter
            form={filterForm}
            applyFilter={applyFilter}
            resetFilter={resetFilter}
          />
        </Col>

        <Col span={24}>
          <Card>
            <Flex
              justify="space-between"
              align="center"
              className="mb-2"
              wrap={true}
              gap={8}
            >
              <Title level={5} className="m-0">
                PARAMETER TRENDING
              </Title>

              <Flex gap={8} wrap={true}>
                <Segmented
                  options={[
                    { value: "Trend", icon: <LineChartOutlined /> },
                    { value: "Table", icon: <BarsOutlined /> },
                  ]}
                  value={trendView}
                  onChange={(value) => setTrendView(value)}
                />

                <Tooltip
                  title={
                    parameterTrendTableData.length === 0 &&
                    parameterTrendSummaryData.length === 0
                      ? "No data to download"
                      : ""
                  }
                >
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadFile}
                    disabled={
                      parameterTrendTableData.length === 0 &&
                      parameterTrendTableData.length === 0
                    }
                  />
                </Tooltip>
              </Flex>
            </Flex>

            {loading ? (
              <div style={{ height: 520 }}>
                <Skeleton />
              </div>
            ) : (
              <div>
                {trendView === "Trend" ? (
                  <>
                    {parameterTrendData.length > 0 ? (
                      <CustomLineChart
                        height={450}
                        data={parameterTrendData}
                        themeToken={token}
                      ></CustomLineChart>
                    ) : (
                      <Flex
                        style={{ height: 450 }}
                        align="center"
                        justify="center"
                      >
                        <Empty />
                      </Flex>
                    )}
                    <SummaryTable
                      data={parameterTrendSummaryData}
                    ></SummaryTable>
                  </>
                ) : (
                  <>
                    <TrendTableView
                      data={parameterTrendTableData}
                      summaryData={parameterTrendSummaryData}
                    />
                  </>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default ParameterTrendPage;
